/**
 * Best-effort source metadata for a browser File (video/*).
 * FPS is usually unavailable from HTMLVideoElement; resolution uses decoded dimensions.
 * @param {File} file
 * @returns {Promise<{ durationSeconds: number | null, sourceVideoResolutionFps: string | null }>}
 */
export function readVideoFileSourceMetadata(file) {
  if (!file || typeof file !== 'object') {
    return Promise.resolve({ durationSeconds: null, sourceVideoResolutionFps: null });
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve({ durationSeconds: null, sourceVideoResolutionFps: null });
  }
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      const finish = (durationSeconds, resolutionFps) => {
        URL.revokeObjectURL(url);
        resolve({ durationSeconds, sourceVideoResolutionFps: resolutionFps });
      };
      video.onloadedmetadata = () => {
        const d = Number(video.duration);
        const durationSeconds =
          Number.isFinite(d) && d > 0 ? d : null;
        const w = Number(video.videoWidth);
        const h = Number(video.videoHeight);
        let sourceVideoResolutionFps = null;
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
          sourceVideoResolutionFps = `${Math.round(w)}x${Math.round(h)}`;
        }
        finish(durationSeconds, sourceVideoResolutionFps);
      };
      video.onerror = () => finish(null, null);
      video.src = url;
    } catch {
      resolve({ durationSeconds: null, sourceVideoResolutionFps: null });
    }
  });
}

/**
 * Best-effort duration (seconds) for a browser File (video/*).
 * @param {File} file
 * @returns {Promise<number | null>}
 */
export function readVideoFileDurationSeconds(file) {
  return readVideoFileSourceMetadata(file).then((m) => m?.durationSeconds ?? null);
}
